import React, {useContext, useEffect, useState} from "react";
import TrackVASContext from "../../TrackVASContext";
import {PageTitle} from "../../../_metronic/layout/core";
import {GenericTable} from "../generic/table/GenericTable";
import {defaultList} from "../generic/InitialStates";
import {ActionsColumnFormatter} from "../../column-formatters/ActionsColumnFormatter";
import {ENTITY_ID_BRANCH} from "../generic/Constants";
import {HeaderColumnFormatter} from "../../column-formatters/HeaderColumnFormatter";
import {showConfirmDialog} from "../../generic-components/ConfirmDialog";
import {genericDelete} from "../generic/crud";
import {showToast, TOAST_SUCCESS} from "../../utils/toast/Toast";
import {BranchAddEditDialog} from "./BranchAddEditDialog";
import {BranchDetailsDialog} from "./BranchDetailsDialog";

export function BranchesPage() {
    const myData = useContext(TrackVASContext);
    const [reload, setReload] = useState(1);
    const [pageLoadComplete, setPageLoadComplete] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showModelId, setShowModelId] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedBranchId, setSelectedBranchId] = useState(null);

    const profileBreadCrumbs = [
        {
            title: "Home",
            path: "/dashboard",
            isSeparator: false,
            isActive: false,
        },
        {
            title: "",
            path: "",
            isSeparator: true,
            isActive: false,
        },
    ];

    // Custom formatter for clickable branch name
    const BranchNameFormatter = (cellContent, row) => {
        return (
            <a
                href="#"
                className="text-primary text-hover-primary fw-bold fs-6"
                style={{
                    textDecoration: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'inline-block'
                }}
                onClick={(e) => {
                    e.preventDefault();
                    openDetailsDialog(row.id);
                }}
            >
                {row.name}
            </a>
        );
    };

    // Generate color from name hash
    const getColorFromName = (name) => {
        const colors = [
            'primary',   // Blue
            'success',   // Green
            'info',      // Cyan
            'warning',   // Yellow/Orange
            'danger',    // Red
        ];

        // Improved hash function for better distribution
        let hash = 0;
        if (!name) return 'primary';

        for (let i = 0; i < name.length; i++) {
            const char = name.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32bit integer
        }

        const index = Math.abs(hash) % colors.length;
        return colors[index];
    };

    // Custom formatter for supervisor names
    const SupervisorsFormatter = (cellContent, row) => {
        if (!row.supervisorName || row.supervisorName.length === 0) {
            return <span className="text-muted">No supervisors</span>;
        }

        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                {row.supervisorName.map((name, index) => (
                    <span
                        key={index}
                        className={`badge badge-${getColorFromName(name)}`}
                        style={{
                            fontSize: '0.85rem',
                            fontWeight: '500',
                            padding: '5px 10px'
                        }}
                    >
                        {name}
                    </span>
                ))}
            </div>
        );
    };

    // Custom formatter for advisor names
    const AdvisorsFormatter = (cellContent, row) => {
        if (!row.advisorNames || row.advisorNames.length === 0) {
            return <span className="text-muted">No advisors</span>;
        }

        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                {row.advisorNames.map((name, index) => (
                    <span
                        key={index}
                        className={`badge badge-${getColorFromName(name)}`}
                        style={{
                            fontSize: '0.85rem',
                            fontWeight: '500',
                            padding: '5px 10px'
                        }}
                    >
                        {name}
                    </span>
                ))}
            </div>
        );
    };

    const columns = [
        {
            dataField: "id",
            text: "Id",
            hidden: true
        }, {
            dataField: "name",
            text: "Branch Name",
            formatter: BranchNameFormatter,
            search: true
        },
        {
            dataField: "dealerName",
            text: "Dealer"
        },
        {
            dataField: "managerName",
            text: "Manager",
            showInMobile: false,
        },
        {
            dataField: "supervisorName",
            text: "Supervisors",
            formatter: SupervisorsFormatter,
        },
        {
            dataField: "advisorNames",
            text: "Service Advisors",
            formatter: AdvisorsFormatter,
            showInMobile: false,
        },
        {
            dataField: "action",
            text: "Actions",
            formatter: ActionsColumnFormatter,
            formatExtraData: {
                buttons:
                    [
                        {
                            name: "Edit",
                            color: "primary",
                            icon: "edit",
                            method: openEditDialog
                        },
                        {
                            name: "Delete",
                            color: "danger",
                            icon: "trash-alt",
                            method: openDeleteDialog
                        }
                    ],
            },
            classes: "text-right pr-0",
            headerClasses: "text-right pr-3",
            style: {
                minWidth: "100px",
            },
        },
    ];

    function openEditDialog(row) {
        setShowModelId(row.id);
        setShowModal(true);
    }

    function openDetailsDialog(branchId) {
        setSelectedBranchId(branchId);
        setShowDetailsModal(true);
    }

    function reloadData() {
        setReload(reload + 1);
    }

    async function openDeleteDialog(row) {
        const result = await showConfirmDialog("Do you really want to delete the Branch? You will be able to delete it only if it is not used anywhere.");
        if (result) {
            genericDelete(ENTITY_ID_BRANCH, row.id).then(() => {
                showToast("Branch is Deleted!", TOAST_SUCCESS);
                reloadData();
            }).catch(() => {
                }
            ).finally(() => {
                reloadData();
            });
        }
    }

    function addButtonClicked() {
        setShowModelId(null);
        setShowModal(true);
    }

    const handleClose = () => setShowModal(false);
    const handleDetailsClose = () => {
        setShowDetailsModal(false);
        setSelectedBranchId(null);
    };

    useEffect(() => {
        myData.setBaseData({
            toolBarButtons: [
                {
                    name: "ADD BRANCH",
                    color: "primary",
                    icon: "fa fa-plus",
                    onClick: addButtonClicked,
                },
            ],
        });

        setPageLoadComplete(true);

        return () => {
            myData.setBaseData({
                toolBarButtons: []
            });
        };
    }, []);

    return (
        <>
            <PageTitle breadcrumbs={profileBreadCrumbs}>Branches</PageTitle>
            <div className="card card-body">
                {pageLoadComplete && (
                    <GenericTable
                        reload={reload}
                        defaultData={defaultList}
                        columns={columns}
                        entityId={ENTITY_ID_BRANCH}
                    />
                )}
            </div>
            <BranchAddEditDialog show={showModal} handleClose={handleClose} id={showModelId} onSave={reloadData}/>
            <BranchDetailsDialog
                show={showDetailsModal}
                handleClose={handleDetailsClose}
                branchId={selectedBranchId}
            />
        </>
    );
}
